import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { GlyphErrorPage, GlyphLoader } from '@weretradeit/ui-react';
import App from './App';
import { marketingLoader } from './api/loaders';

/**
 * Der Routenbaum — EINMAL, und von main.tsx wie von App.test.tsx benutzt.
 *
 * ── Warum getrennt von main.tsx ─────────────────────────────────────────────
 *
 * Bis zum 08.09.2026 stand der Baum in main.tsx, und er bestand aus genau einer
 * Route:
 *
 *   { path: '/', element: <App />, loader: marketingLoader }
 *
 * Der Test baute sich daneben SEINEN eigenen Baum (`path: '*'`, ein Element).
 * Beide Seiten beschrieben damit eine Anwendung, die es so nicht gab: unter
 * `src/pages/` lagen vierzehn Seiten, die keine Route je gezeichnet hat, und
 * `App.test.tsx` pruefte sieben Faelle gegen Pfade (`/newsletter`, `/social`),
 * die nirgends registriert waren. Ein Test, der seinen eigenen Router mitbringt,
 * kann die Verdrahtung der Anwendung nicht pruefen — er prueft seine eigene.
 *
 * Jetzt gibt es eine Quelle. Wer hier eine Route hinzufuegt, hat sie in der
 * Anwendung UND im Test; wer eine vergisst, faellt in beiden auf.
 *
 * ── Was NICHT hier steht ───────────────────────────────────────────────────
 *
 * `pages/affiliate/**` (vier Seiten, 1359 Zeilen) ist am 08.09.2026 geloescht
 * worden statt verkabelt: alle vier importierten `../api/client`, ein Modul,
 * das in diesem Repo nie existiert hat. Sie liessen sich nicht uebersetzen, und
 * frontend/tsconfig.json trug eigens eine Ausnahme, damit der Typcheck sie nicht
 * sieht. Diese Ausnahme ist mit dem Verzeichnis entfallen.
 *
 * `pages/Social/{Analytics,Calendar,Campaigns}` sind ebenfalls nicht
 * zurueckgekommen: sie waren „Coming Soon" — eine Ueberschrift und eine
 * Aufzaehlung dessen, was es nicht gibt. Ein Menueeintrag, der auf ein
 * Versprechen zeigt, ist schlechter als keiner.
 */

const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const NewsletterPage = lazy(() => import('./pages/NewsletterPage').then((m) => ({ default: m.NewsletterPage })));
const AffiliatePage = lazy(() => import('./pages/AffiliatePage').then((m) => ({ default: m.AffiliatePage })));
const DealsSection = lazy(() => import('./pages/deals').then((m) => ({ default: m.DealsSection })));
const DealsPage = lazy(() => import('./pages/deals/DealsPage').then((m) => ({ default: m.DealsPage })));
const PartnersPage = lazy(() => import('./pages/deals/PartnersPage').then((m) => ({ default: m.PartnersPage })));
const ScansPage = lazy(() => import('./pages/deals/ScansPage').then((m) => ({ default: m.ScansPage })));
const SocialPage = lazy(() => import('./pages/Social').then((m) => ({ default: m.SocialPage })));
const SocialDashboard = lazy(() => import('./pages/Social/Dashboard').then((m) => ({ default: m.SocialDashboard })));
const SocialComposer = lazy(() => import('./pages/Social/Composer').then((m) => ({ default: m.SocialComposer })));
const SocialAccounts = lazy(() => import('./pages/Social/Accounts').then((m) => ({ default: m.SocialAccounts })));

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <App />,
    /* DIE WARNUNG, DIE HIER VERSCHWINDET
     *
     *   No `HydrateFallback` element provided to render during initial hydration
     *
     * react-router meldet sie, sobald der erste Aufruf einen Loader abwarten
     * muss — hier `marketingLoader` an der Indexroute. Bis dahin gibt es nichts
     * zu zeichnen, und ohne Rueckfallelement ist dieses „nichts" ein LEERES
     * Dokument: weisse Seite, keine Kopfzeile, kein Wartezeichen, fuer die
     * volle Dauer des Ladens.
     *
     * Es ist derselbe Zustand, den `App.test.tsx` mit dem Warten auf
     * `router.state.initialized` umgeht — dort sichtbar geworden, hier als
     * Konsolenmeldung.
     *
     * Bewusst NICHT `<App />` mit leeren Daten: der Rahmen liest
     * `useLoaderData()`, das waere zur Hydrierung noch undefined.
     *
     * Seit 4.2.0 `GlyphLoader` statt des Kreisels: es ist der erste
     * Ladebildschirm eines harten Aufrufs, und gleich danach folgt die
     * Sitzungspruefung in App.tsx mit derselben Szene — ohne den Wechsel
     * Kreisel -> Szene. Der Text bleibt „Wird geladen" (App.test.tsx wartet
     * darauf, dass er verschwindet). Das Feld selbst bleibt HIER leer: die
     * Engine laedt erst App.tsx (glyphScene.ts) — liefe sie schon jetzt,
     * naehme sie dem Uebergang zur App den Hauptthread. */
    hydrateFallbackElement: <GlyphLoader appKey="marketShark" label="Wird geladen" />,
    children: [
      // Der Loader haengt an der Kindroute, nicht am Rahmen: nur das Dashboard
      // braucht die Marketing-Kennzahlen. Am Rahmen haette er sie bei JEDEM
      // Seitenwechsel neu geholt, auch fuer /social.
      { index: true, element: <Dashboard />, loader: marketingLoader },
      { path: 'newsletter', element: <NewsletterPage /> },
      // Partnerertraege aus AWIN. Eigene Route und NICHT eine sechste Kachel
      // auf dem Dashboard: eine AWIN-Abfrage ueber 90 Tage sind drei Aufrufe an
      // eine fremde API, und die haetten dann bei JEDEM Dashboard-Aufruf
      // stattgefunden — fuer Zahlen, die ihre eigene Seite haben.
      { path: 'affiliate', element: <AffiliatePage /> },
      /* Angebotsjagd. Bewusst GETRENNT von `/affiliate`: dort steht, was die
         Partnernetze uns zahlen (AWIN), hier wird nach Angeboten gesucht,
         bewertet und freigegeben. Zwei Produkte, die nur ein Wort teilen. */
      {
        path: 'deals',
        element: <DealsSection />,
        children: [
          { index: true, element: <DealsPage /> },
          { path: 'partners', element: <PartnersPage /> },
          { path: 'scans', element: <ScansPage /> },
        ],
      },
      {
        path: 'social',
        element: <SocialPage />,
        children: [
          { index: true, element: <SocialDashboard /> },
          { path: 'composer', element: <SocialComposer /> },
          { path: 'accounts', element: <SocialAccounts /> },
        ],
      },
      /* 4.2.0: die Fehlerseite der Flotte mit der eigenen Szene
         (GlyphErrorPage, Vorgabe kind="404" — die Szene ruht, nichts ist
         kaputt, die Adresse fehlt). Bis hierher gab es keinen Auffang: eine
         unbekannte Adresse landete auf der ungestylten Vorgabeseite von
         react-router, OHNE Kopfzeile. Jetzt INNERHALB des Rahmens: Kopfzeile
         und Menue stehen weiter da, und die Seite liegt hinter derselben
         Anmeldung wie jede andere. */
      { path: '*', element: <GlyphErrorPage appKey="marketShark" /> },
    ],
  },
];
